import store from '@/store';
import router from '@/router';
import { validateSession } from '@/services/auth-service';
import { leaveAgentVoiceChannel } from '@/services/channel-service';
import Client, {
  reconnectWebSocketStomp,
  isWebSocketConnected,
  unsubscribeWebSocketStompAndDisconnect,
} from '@/weon/message-broker/Client';
import { connectWebRTC, endWebRTCSession } from '@/weon/web-rtc/WeonWebRTC.js';
import CallAction from '@/weon/actions/CallAction';

let retry = 0;

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    if (window.janusInstance != null && !window.janusInstance.isConnected()) {
      if (window.weonWebRTC != null) {
        window.weonWebRTC.iceState('disconnected');
      }
    }
  } else {
    store.dispatch('channel/setLatestStatusBeforeDisconnect');
  }
});

export const resetVoiceConnection = async () => {
  if (store.state.attendance.Attendance.agentPreferences.webRtcAutoReconnect) {
    // console.log('[WEON-WEBRTC][CONNECTION-LOST] - Conexão Weon WebRTC perdida! Tentando reconectar...')
    await endWebRTCSession();

    const agentWebSessionStatus = await agentWebSessionChecker();

    if (
      (agentWebSessionStatus == 'logged' &&
        store.state._connection.connectionWebRTCStatus == 'connection-lost') ||
      store.state._connection.connectionWebRTCStatus == 'disconnected'
    ) {
      await leaveAgentVoiceChannel(store.state.attendance.Attendance.agentId);
      await unsubscribeWebSocketStompAndDisconnect();
      await reconnectWebSocketStomp();
      connectWebRTC(
        store.state.attendance.Attendance.agentId,
        store.state.attendance.Attendance.org_unit_id,
      );
    } else {
      if (store.state._connection.connectionWebRTCStatus != 'connected') {
        setTimeout(() => {
          window.webRTCReconnectionRetries++;
          // console.log('[WEON-WEBRTC][CONNECTION-LOST][TRIES] - Tentativa de reconexão do WebRTC: ',window.webRTCReconnectionRetries )
          resetVoiceConnection();
        }, 2000);
      }
    }
  }
};

/**
 * tryReconnect
 * Realiza a tentativa de reconexão automática do sistema
 * @returns Boolean
 */
export const tryReconnect = async () => {
  try {
    // console.log('[tryReconnect] Verificando conexão do agente.');

    if (
      store.state.attendance.Attendance.agentId &&
      store.state.attendance.Attendance.agentId !== '' &&
      store.state.attendance.Attendance.agentId !== undefined
    ) {
      // console.log('[tryReconnect] Reconexão tentativa: ',retry)
      const agentWebSessionStatus = await agentWebSessionChecker();

      if (agentWebSessionStatus === 'logged') {
        retry = 0;
        // console.log('[tryReconnect] Sessão do agente está logada.');
        if (!isWebSocketConnected()) {
          // console.log('[tryReconnect] WebSocket não está conectado. Tentando reconectar.');
          try {
            await reconnectWebSocketStomp();
          } catch (error) {
            // console.log('[tryReconnect] Erro ao tentar conectar no WebSocket.');
          }
        }

        if (!store.state._connection.reconnectionConfirmation) {
          // console.log('[tryReconnect] Ocultando diálogo de problema de conexão.')
          store.dispatch('_connection/showConnectionProblemDialog', false);
        }
        // console.log('[tryReconnect] Conexão Weon disponível.')
        store.dispatch('_connection/setWeonConnectionAvailable', true);
        return;
      }

      if (agentWebSessionStatus === 'timeout' && retry >= 5) {
        // console.log('[tryReconnect] Timeout na sessão do agente. Exibindo diálogo de problema de conexão.')
        store.dispatch('_connection/showConnectionProblemDialog', true);
        store.dispatch('_connection/setWeonConnectionAvailable', false);
      }

      if (
        agentWebSessionStatus === 'expired' ||
        agentWebSessionStatus === 'invalidToken'
      ) {
        // console.log('[tryReconnect] Sessão do agente expirada. Realizando logoff.')
        await store.dispatch('auth/logoff');
        await endWebRTCSession();
        router.push('/login');
        return;
      }
    }
  } catch (error) {
    // console.error('[tryReconnect] Erro durante tentativa de reconexão:', error)
  }
};

/**
 * agentWebSessionChecker
 * Verifica se a sessao é válida
 * @returns String
 */
export const agentWebSessionChecker = async () => {
  const internetStatus = navigator.onLine;

  const requestData = {
    agent_id: store.state.attendance.Attendance.agentId,
    org_unit_id: store.state.attendance.Attendance.org_unit_id,
  };

  const agentWebSessionResponse = await validateSession(requestData)
    .then((response) => {
      if (response.status == 200 && response.data.hasOwnProperty('success')) {
        return 'logged';
      }
    })
    .catch((error) => {
      retry++;
      if (error.response) {
        if (
          error.response.status == 401 &&
          error.response.data.hasOwnProperty('cause')
        ) {
          return error.response.data.cause;
        }
      } else if (error.request) {
        return 'timeout';
      } else {
        return 'timeout';
      }
    });

  if (
    internetStatus &&
    ['logged', 'expired'].includes(agentWebSessionResponse)
  ) {
    store.dispatch('_connection/setInternetConnectionAvailable', true);
  } else {
    store.dispatch('_connection/setInternetConnectionAvailable', false);
  }

  return agentWebSessionResponse;
};

export const sendPongEvent = async () => {
  try {
    if (window.weonWebSocket != null) {
      const agentId = store.state.attendance.Attendance.agentId;
      const queueAgent = store.state.system.queueListenerMessageBroker;

      if (!agentId || !queueAgent) return;

      if (
        window.weonWebSocket[queueAgent] &&
        window.weonWebSocket[queueAgent].client
      ) {
        const queueAgentClient = window.weonWebSocket[queueAgent].client;

        if (
          queueAgentClient.connected &&
          queueAgentClient.ws.readyState === 1
        ) {
          const queuesToSend = ['COMMUNICATOR', 'WHATSAPP'];
          queuesToSend.forEach((queue) => {
            new Client(queueAgent).client.send(
              queue,
              null,
              JSON.stringify(CallAction.sendPongBroker(agentId)),
            );
          });
          new Client(queueAgent).client.send(
            'CHATVISOR',
            null,
            JSON.stringify(CallAction.sendPongChatvisorBrokerAction(agentId)),
          );
          // console.log('[WEON-WEBSOCKET] - PONG enviado para o backend (COMMUNICATOR,WHATSAPP,CHATVISOR).')
        }
      }
    } else {
      // console.log('[WEON-WEBSOCKET] - WebSocket não iniciado.')
    }
  } catch (error) {
    // console.error('[sendPongEvent] Erro durante envio de PONG:', error);
  }
};
